/**
 * Typed client for `/api/sales/*` (the dashboard server's proxy to the
 * autonomous sales agent). v2 endpoints power the four-tab native
 * shell; v0 endpoints keep the Brain tab's source-health widgets
 * working during the rebuild.
 *
 * Every call goes through the shared `api<T>()` wrapper so it picks
 * up token refresh, ApiError mapping, and JWT attachment. Every
 * round-trip is also wrapped in a tiny dev-mode logger so an operator
 * watching the devtools console can see exactly which v2 endpoint
 * succeeded or failed and with which typed `code`.
 */
import type {
  SalesAddToCrmRequest,
  SalesAgentControl,
  SalesAgentHealth,
  SalesAgentMode,
  SalesLeadFocus,
  SalesLeadFocusInput,
  SalesBrainFeedPayload,
  SalesBrainMissionsPayload,
  SalesCapabilityCatalogPayload,
  SalesCapabilityProposalsPayload,
  SalesHarvestActivityPayload,
  SalesHarvestContactsPayload,
  SalesHarvestOrgDetail,
  SalesHarvestOrgsPayload,
  SalesCampaign,
  SalesCampaignInput,
  SalesCampaignsPayload,
  SalesCampaignContactGroupResult,
  SalesCampaignReport,
  SalesHarvestStats,
  SalesHarvestStrategy,
  SalesLeadSource,
  SalesLeadsListPayload,
  SalesOrgLeadDetail,
  SalesShareLeadRequest,
  SalesShareLeadResponse,
  SalesShareRecipientsResponse,
  SalesSignalKind,
  SalesSteerApplyResult,
  SalesSteerPatch,
  SalesSteerPreview,
  SalesSteeringAck,
  SalesSteeringTarget,
  SalesStrategyDocument,
  SalesStrategyFold,
  SalesStrategyHistoryPayload,
  SalesStrategyRefreshAck,
  SalesSteeringActivePayload,
  SalesTodayPayload,
  SalesV2Ack,
  SalesVertical,
} from "@xop/dashboard-shared";

import { api, ApiError } from "../../lib/api.js";
import { devError, devLog } from "./devLog.js";

async function tracked<T>(label: string, run: () => Promise<T>): Promise<T> {
  devLog(`client/${label}:start`);
  try {
    const out = await run();
    devLog(`client/${label}:ok`);
    return out;
  } catch (e) {
    if (e instanceof ApiError) {
      devError(`client/${label}:error`, {
        status: e.status,
        code: e.code,
        message: e.message,
        details: e.details,
      });
    } else {
      devError(`client/${label}:error`, e);
    }
    throw e;
  }
}

export interface ListLeadsParams {
  q?: string;
  vertical?: SalesVertical;
  country?: string;
  signal_kind?: SalesSignalKind;
  min_confidence?: number;
  status?: string;
  /** Filter by which agent created the lead: "discovery" (the Lead Generator) or
   * "harvester" (promoted from the Harvester pool). Omit for both. Forwarded to
   * the agent's `/v2/leads?source=`. */
  source?: SalesLeadSource;
  /** ISO 8601 lower bound on `created_at`. The Today tab passes local
   * midnight to show only leads added today; the agent's `total` reflects
   * the real recency count (never capped by `per_page`). */
  created_after?: string;
  /** ISO 8601 EXCLUSIVE upper bound on `created_at` — with `created_after`
   * it makes the closed "Last month" window. */
  created_before?: string;
  /** Ordering override. `"created_at"` = newest-added first (what the
   * Today "leads added today" list wants); `"confidence"`, `"name"`,
   * `"country"`, `"vertical"`, `"source"`, `"last_signal_at"` order the WHOLE
   * dataset by the matching Leads-table column; omit for the Leads tab's
   * default freshest-signal/urgency order. See `leads/leadSort.ts`. */
  sort?: string;
  /** Direction for `sort` — `"asc"` | `"desc"` (default `"desc"` server-side).
   * Set by the Leads-table column header so a click sorts the entire database,
   * not just the visible page. */
  sort_dir?: string;
  /** "Closing soon" filter — when set, return only LIVE leads whose dated
   * deadline falls within this many days (today..today+N, non-expired),
   * ordered soonest-first. Powers the Leads-page "Closing soon" tab + its
   * count; forwarded to the agent's `/v2/leads`. */
  closing_within_days?: number;
  page?: number;
  per_page?: number;
}

/* ── Contact-harvester pool (Contact Directory tab) ───────────────── */
export interface HarvestOrgsParams {
  q?: string;
  vertical?: string;
  campaign?: string;
  status?: string;
  has_contacts?: boolean;
  page?: number;
  per_page?: number;
}

export interface HarvestContactsParams {
  q?: string;
  vertical?: string;
  campaign?: string;
  seniority?: string;
  has_email?: boolean;
  page?: number;
  per_page?: number;
}

/* ── Capabilities / "what the agent knows" (Knowledge tab) ───────── */
export interface SalesSkill {
  name: string;
  category: string;
  description: string;
}
export interface SalesDataAccess {
  source: string;
  scope: string;
}
export interface SalesCompanyKnowledge {
  version: number | null;
  source_type: string | null;
  pages_ingested: number | null;
  learned_at: string | null;
  products: string[];
  verticals: string[];
  target_buyers: string[];
  geographies: string[];
  differentiators: string[];
  summary: string;
}
export interface SalesCapabilities {
  skills: SalesSkill[];
  data_access: SalesDataAccess[];
  cannot: string[];
  crm_directory_enabled: boolean;
  company: SalesCompanyKnowledge | null;
}

/* ── Economics / spend ("what the agent costs" — Brain/Today strip) ── */
export interface SalesEconomics {
  since: string;
  total_cost_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  by_caller: Record<string, number>;
  /**
   * Per-BOT rollup of `by_caller`, computed server-side (xop-agents
   * `app/services/llm_bots.py`). Keys: `harvester`, `lead_generator`,
   * `other`. Never derive this in the SPA from caller tags — the old
   * "harvester = harvest_enumerate + harvest_dig, everything else is the
   * lead generator" split booked five harvester tags (incl. the paid
   * `email_verify` checks) to the lead generator.
   * Optional: an agent deployed before 2026-09-05 doesn't send it.
   */
  by_bot?: Record<string, number>;
  by_model: Record<string, number>;
  /** Calls that burned tokens but priced at $0 — a model missing from the
   * gateway's price table, i.e. real spend the totals here do NOT contain. */
  unpriced_calls?: number;
  unpriced_models?: string[];
  leads_today: number;
  cost_per_lead_usd: number | null;
  budget_usd: number;
  over_budget: boolean;
  /** Current budget-window start (ISO) and the next reset / "wake" time (ISO)
   * — drive the Economics strip's live "sleeps in / wakes in" countdown. */
  window_start?: string;
  resets_at?: string;
}

/* ── Quality ("how good the leads are" — the LLM-judge eval layer) ─── */
export interface SalesQuality {
  evaluated: number;
  avg_grounding: number | null;
  avg_fit: number | null;
  verdicts: { keep: number; review: number; reject: number };
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

export const salesApi = {
  /* ── Backend health ────────────────────────────────────────────── */
  getHealth: () =>
    tracked("getHealth", () => api<SalesAgentHealth>("/api/sales/health")),

  /* ── v2 — autonomous agent ─────────────────────────────────────── */
  getToday: () =>
    tracked("getToday", () => api<SalesTodayPayload>("/api/sales/v2/today")),

  listLeads: (p: ListLeadsParams = {}) =>
    tracked("listLeads", () =>
      api<SalesLeadsListPayload>(
        `/api/sales/v2/leads${qs(p as Record<string, unknown>)}`,
      ),
    ),

  getLead: (id: string) =>
    tracked(`getLead(${id})`, () =>
      api<SalesOrgLeadDetail>(`/api/sales/v2/leads/${encodeURIComponent(id)}`),
    ),

  // Move a lead through the pipeline: new | qualified | contacted | denied
  // | dismissed. Drives the Leads-page tabs.
  setLeadStatus: (id: string, status: string) =>
    tracked(`setLeadStatus(${id},${status})`, () =>
      api<SalesV2Ack>(`/api/sales/v2/leads/${encodeURIComponent(id)}/status`, {
        method: "POST",
        body: { status },
      }),
    ),

  // Re-classify a lead's vertical (its sector "classification"). Editable
  // inline from the Sales leads list; the agent stores the free-text vertical
  // and the CRM industry derives from it on "Qualify for CRM".
  setLeadVertical: (id: string, vertical: string) =>
    tracked(`setLeadVertical(${id},${vertical})`, () =>
      api<SalesV2Ack>(`/api/sales/v2/leads/${encodeURIComponent(id)}/vertical`, {
        method: "POST",
        body: { vertical },
      }),
    ),

  // ── Share a lead by email (PDF generated client-side, attached server-side) ──
  // Suggested recipients = dashboard users with a sales-facing role.
  listShareRecipients: () =>
    tracked("listShareRecipients", () =>
      api<SalesShareRecipientsResponse>("/api/sales/share/recipients"),
    ),

  shareLead: (id: string, payload: SalesShareLeadRequest) =>
    tracked(`shareLead(${id})`, () =>
      api<SalesShareLeadResponse>(
        `/api/sales/leads/${encodeURIComponent(id)}/share`,
        { method: "POST", body: payload },
      ),
    ),

  /** Push a lead into the CRM as a Lead (the server creates it with the
   * operator's CRM identity, then the agent records it as EXPORTED). */
  addLeadToCrm: (
    id: string,
    lead: SalesAddToCrmRequest,
  ) =>
    tracked(`addLeadToCrm(${id})`, () =>
      api<{ message: string; crm_external_id: string }>(
        `/api/sales/v2/leads/${encodeURIComponent(id)}/add-to-crm`,
        { method: "POST", body: lead },
      ),
    ),

  // What the agent can do / can see / has learned (Knowledge tab).
  getCapabilities: () =>
    tracked("getCapabilities", () =>
      api<SalesCapabilities>("/api/sales/v2/capabilities"),
    ),

  // The curated capability→vertical catalog (read-only) the agent already hunts.
  getCapabilityCatalog: () =>
    tracked("getCapabilityCatalog", () =>
      api<SalesCapabilityCatalogPayload>("/api/sales/v2/capabilities/catalog"),
    ),

  // Agent-proposed NEW capability→vertical fits awaiting operator review.
  listCapabilityProposals: () =>
    tracked("listCapabilityProposals", () =>
      api<SalesCapabilityProposalsPayload>("/api/sales/v2/capabilities/proposals"),
    ),

  approveCapabilityProposal: (id: string) =>
    tracked(`approveCapabilityProposal(${id})`, () =>
      api<SalesV2Ack>(
        `/api/sales/v2/capabilities/proposals/${encodeURIComponent(id)}/approve`,
        { method: "POST" },
      ),
    ),

  rejectCapabilityProposal: (id: string) =>
    tracked(`rejectCapabilityProposal(${id})`, () =>
      api<SalesV2Ack>(
        `/api/sales/v2/capabilities/proposals/${encodeURIComponent(id)}/reject`,
        { method: "POST" },
      ),
    ),

  // Today's LLM spend (cost by caller/model, cost-per-lead, budget status).
  getEconomics: () =>
    tracked("getEconomics", () =>
      api<SalesEconomics>("/api/sales/v2/economics"),
    ),

  // Lead quality from the LLM-judge (avg grounding + fit, verdict split).
  getQuality: () =>
    tracked("getQuality", () => api<SalesQuality>("/api/sales/v2/quality")),

  getStrategy: () =>
    tracked("getStrategy", () =>
      api<SalesStrategyDocument | null>("/api/sales/v2/strategy"),
    ),

  getStrategyVersion: (id: string) =>
    tracked(`getStrategyVersion(${id})`, () =>
      api<SalesStrategyDocument>(
        `/api/sales/v2/strategy/${encodeURIComponent(id)}`,
      ),
    ),

  getStrategyHistory: () =>
    tracked("getStrategyHistory", () =>
      api<SalesStrategyHistoryPayload>("/api/sales/v2/strategy/history"),
    ),

  refreshStrategy: () =>
    tracked("refreshStrategy", () =>
      api<SalesStrategyRefreshAck>("/api/sales/v2/strategy/refresh", { method: "POST" }),
    ),

  /** The newest strategy regeneration — steer-driven or on-demand. Null before
   *  anything has been asked for. */
  getStrategyRegen: () =>
    tracked("getStrategyRegen", () =>
      api<SalesStrategyFold | null>("/api/sales/v2/strategy/regen"),
    ),

  // `target` picks which agent the steer aims at: "leads" (Lead Generator, the
  // default), "harvester" (the contact Harvester — it parses the note for
  // region/facility-type, e.g. "harvest just the US"), or "both".
  postSteeringNote: (body: string, target: SalesSteeringTarget = "leads") =>
    tracked("postSteeringNote", () =>
      api<SalesSteeringAck>("/api/sales/v2/steering", {
        method: "POST",
        body: { body, target },
      }),
    ),

  /** Ask the agent to fold a directive into the strategy again, after a fold
   *  gave up. The note itself is untouched — it was saved all along. */
  refoldSteering: (id: string) =>
    tracked(`refoldSteering(${id})`, () =>
      api<SalesSteeringAck>(`/api/sales/v2/steering/${encodeURIComponent(id)}/refold`, {
        method: "POST",
      }),
    ),

  // ── Steering directives (standing orders the agent honours until
  //    archived). The Today tab's "Active directives" panel reads the
  //    active list and lets the operator archive / restore. ──────────
  listActiveSteering: () =>
    tracked("listActiveSteering", () =>
      api<SalesSteeringActivePayload>("/api/sales/v2/steering/active"),
    ),

  archiveSteering: (id: string) =>
    tracked(`archiveSteering(${id})`, () =>
      api<SalesV2Ack>(
        `/api/sales/v2/steering/${encodeURIComponent(id)}/archive`,
        { method: "POST" },
      ),
    ),

  unarchiveSteering: (id: string) =>
    tracked(`unarchiveSteering(${id})`, () =>
      api<SalesV2Ack>(
        `/api/sales/v2/steering/${encodeURIComponent(id)}/unarchive`,
        { method: "POST" },
      ),
    ),

  getBrainFeed: () =>
    tracked("getBrainFeed", () =>
      api<SalesBrainFeedPayload>("/api/sales/v2/brain/feed"),
    ),

  getBrainMissions: () =>
    tracked("getBrainMissions", () =>
      api<SalesBrainMissionsPayload>("/api/sales/v2/brain/missions"),
    ),

  runMission: (id: string) =>
    tracked(`runMission(${id})`, () =>
      api<SalesV2Ack>(
        `/api/sales/v2/brain/missions/${encodeURIComponent(id)}/run`,
        { method: "POST" },
      ),
    ),

  // ── Pause / resume the agent (operator control) ───────────────────
  getAgentControl: () =>
    tracked("getAgentControl", () =>
      api<SalesAgentControl>("/api/sales/v2/agent/control"),
    ),

  pauseAgent: (reason: string) =>
    tracked("pauseAgent", () =>
      // The shared `api()` wrapper already JSON-stringifies the body
      // and sets Content-Type: application/json. Passing a pre-
      // stringified body produced a double-encoded payload that
      // Fastify rejected with 415 FST_ERR_CTP_INVALID_MEDIA_TYPE.
      api<SalesAgentControl>("/api/sales/v2/agent/control/pause", {
        method: "POST",
        body: { reason },
      }),
    ),

  resumeAgent: () =>
    tracked("resumeAgent", () =>
      api<SalesAgentControl>("/api/sales/v2/agent/control/resume", {
        method: "POST",
      }),
    ),

  // Switch which track(s) run: "leads" | "contacts" | "both".
  setAgentMode: (mode: SalesAgentMode) =>
    tracked(`setAgentMode(${mode})`, () =>
      api<SalesAgentControl>("/api/sales/v2/agent/control/mode", {
        method: "POST",
        body: { mode },
      }),
    ),

  // Lead focus — where the Lead Generator hunts (countries × verticals).
  getLeadFocus: () =>
    tracked("getLeadFocus", () => api<SalesLeadFocus>("/api/sales/v2/agent/focus")),

  setLeadFocus: (focus: SalesLeadFocusInput) =>
    tracked("setLeadFocus", () =>
      api<SalesLeadFocus>("/api/sales/v2/agent/focus", { method: "POST", body: focus }),
    ),

  // ── Contact-harvester pool (read-only) ────────────────────────────
  getHarvestOrgs: (params: HarvestOrgsParams = {}) =>
    tracked("getHarvestOrgs", () =>
      api<SalesHarvestOrgsPayload>(
        `/api/sales/v2/harvest/orgs${qs(params as Record<string, unknown>)}`,
      ),
    ),

  getHarvestOrg: (id: string) =>
    tracked(`getHarvestOrg(${id})`, () =>
      api<SalesHarvestOrgDetail>(`/api/sales/v2/harvest/orgs/${encodeURIComponent(id)}`),
    ),

  getHarvestContacts: (params: HarvestContactsParams = {}) =>
    tracked("getHarvestContacts", () =>
      api<SalesHarvestContactsPayload>(
        `/api/sales/v2/harvest/contacts${qs(params as Record<string, unknown>)}`,
      ),
    ),

  getHarvestStats: () =>
    tracked("getHarvestStats", () =>
      api<SalesHarvestStats>("/api/sales/v2/harvest/stats"),
    ),

  // NB: `api()` serialises the `body` option itself — pass a plain OBJECT.
  // Pre-stringifying serialises it twice, so the server receives a JSON
  // *string* where it expects an object and FastAPI answers 422 before any
  // handler runs. That was live in production on 2026-08-07 for the steer
  // routes and (silently, since it was written) for campaign create/update.
  // Pinned by client.test.ts.
  // ── Harvest campaigns (Campaigns tab) ─────────────────────────────
  // Operator-defined, time-boxed target sets. Writes are sales:edit on the
  // server: activating a campaign redirects the whole harvester's budget.
  getCampaigns: () =>
    tracked("getCampaigns", () =>
      api<SalesCampaignsPayload>("/api/sales/v2/harvest/campaigns"),
    ),

  getCampaignReport: (slug: string) =>
    tracked(`getCampaignReport(${slug})`, () =>
      api<SalesCampaignReport>(
        `/api/sales/v2/harvest/campaigns/${encodeURIComponent(slug)}/report`,
      ),
    ),

  // One campaign's sendable contacts → one CRM contact group ("Campaign:
  // <label>") for Email Campaigns. Rebuilds the same group on every press, so
  // it only ever holds that campaign. Needs sales:edit + crm:view.
  createCampaignContactGroup: (slug: string) =>
    tracked(`createCampaignContactGroup(${slug})`, () =>
      api<SalesCampaignContactGroupResult>(
        `/api/sales/v2/harvest/campaigns/${encodeURIComponent(slug)}/contact-group`,
        { method: "POST" },
      ),
    ),

  createCampaign: (body: SalesCampaignInput) =>
    tracked("createCampaign", () =>
      api<SalesCampaign>("/api/sales/v2/harvest/campaigns", {
        method: "POST",
        body,
      }),
    ),

  // PATCH semantics: an omitted field is left alone. Never send a full object
  // built from defaults — blanking `verticals` would stop the sweep.
  updateCampaign: (slug: string, body: Partial<SalesCampaignInput>) =>
    tracked(`updateCampaign(${slug})`, () =>
      api<SalesCampaign>(
        `/api/sales/v2/harvest/campaigns/${encodeURIComponent(slug)}`,
        { method: "PATCH", body },
      ),
    ),

  // ── Steering the harvester = editing the live campaign (2026-08-07) ──
  // The note is resolved server-side into a patch on the live campaign;
  // `previewSteer` writes nothing, and `applySteer` sends back the patch the
  // operator actually SAW rather than the note, so a second resolution can
  // never differ from what was on screen.
  previewSteer: (body: string) =>
    tracked("previewSteer", () =>
      api<SalesSteerPreview>("/api/sales/v2/harvest/steer/preview", {
        method: "POST",
        body: { body },
      }),
    ),

  applySteer: (body: string, patch: SalesSteerPatch, target: SalesSteeringTarget) =>
    tracked("applySteer", () =>
      api<SalesSteerApplyResult>("/api/sales/v2/harvest/steer/apply", {
        method: "POST",
        body: { body, patch, target },
      }),
    ),

  deleteCampaign: (slug: string) =>
    tracked(`deleteCampaign(${slug})`, () =>
      api<{ deleted: string; results_retained: boolean }>(
        `/api/sales/v2/harvest/campaigns/${encodeURIComponent(slug)}`,
        { method: "DELETE" },
      ),
    ),

  // The harvester's strategy (shown on the Strategy tab in contacts mode).
  getHarvestStrategy: () =>
    tracked("getHarvestStrategy", () =>
      api<SalesHarvestStrategy>("/api/sales/v2/harvest/strategy"),
    ),

  // The harvester's recent "thinking" (enumerate / dig / dedup / promote log).
  getHarvestActivity: () =>
    tracked("getHarvestActivity", () =>
      api<SalesHarvestActivityPayload>("/api/sales/v2/harvest/activity?limit=60"),
    ),

  // On-demand: verify ONE contact's email through the paid verifier now.
  verifyContact: (id: string) =>
    tracked(`verifyContact(${id})`, () =>
      api<{ id: string; email_status: string }>(
        `/api/sales/v2/harvest/contacts/${encodeURIComponent(id)}/verify`,
        { method: "POST" },
      ),
    ),

};
